import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import { 
  Key, Plus, Trash2, Eye, EyeOff, Shield, Lock,
  Loader2, CheckCircle, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARRIERS = [
  // Shipping Carriers
  { value: 'shipstation', label: 'ShipStation', description: 'Connect your ShipStation account', category: 'shipping' },
  { value: 'easypost', label: 'EasyPost', description: 'Multi-carrier shipping API', category: 'shipping' },
  { value: 'easyship', label: 'EasyShip', description: 'Global shipping platform', category: 'shipping' },
  { value: 'endicia', label: 'Endicia / Stamps.com', description: 'USPS shipping', category: 'shipping' },
  { value: 'sendle', label: 'Sendle', description: 'Carbon neutral shipping', category: 'shipping' },
  { value: 'usps', label: 'USPS', description: 'US Postal Service direct', category: 'shipping' },
  { value: 'ups', label: 'UPS', description: 'United Parcel Service', category: 'shipping' },
  { value: 'fedex', label: 'FedEx', description: 'FedEx shipping services', category: 'shipping' },
  { value: 'dhl', label: 'DHL', description: 'DHL Express worldwide', category: 'shipping' },
  
  // Fraud Analysis & Verification
  { value: 'maxmind', label: 'MaxMind', description: 'GeoIP & minFraud detection', category: 'fraud' },
  { value: 'ipqs', label: 'IPQS', description: 'IP Quality Score fraud prevention', category: 'fraud' },
  { value: 'sift', label: 'Sift', description: 'Digital trust & safety', category: 'fraud' },
  { value: 'signifyd', label: 'Signifyd', description: 'Commerce protection', category: 'fraud' },
  { value: 'kount', label: 'Kount', description: 'Identity trust solutions', category: 'fraud' },
  { value: 'riskified', label: 'Riskified', description: 'Ecommerce fraud prevention', category: 'fraud' },
  
  // Address & Identity Verification
  { value: 'smartystreets', label: 'SmartyStreets', description: 'Address validation & verification', category: 'verification' },
  { value: 'lob', label: 'Lob', description: 'Address verification & mail', category: 'verification' },
  { value: 'melissa', label: 'Melissa', description: 'Data quality & address verification', category: 'verification' },
  { value: 'plaid', label: 'Plaid', description: 'Identity verification', category: 'verification' },
  { value: 'persona', label: 'Persona', description: 'Identity infrastructure', category: 'verification' },
  
  // Custom / Other
  { value: 'sellepack', label: 'SelleGard Freight', description: 'SelleSend carrier integration', category: 'custom' },
  { value: 'custom', label: 'Custom API', description: 'Add your own API integration', category: 'custom' },
];

const SecretsPage = () => {
  const [secrets, setSecrets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showKey, setShowKey] = useState({});
  
  const [formData, setFormData] = useState({
    provider: '',
    api_key: '',
    api_secret: ''
  });

  const { getAuthHeaders } = useAuth();

  const fetchSecrets = async () => {
    try {
      const response = await axios.get(`${API}/secrets`, {
        headers: getAuthHeaders()
      });
      setSecrets(response.data);
    } catch (error) {
      toast.error('Failed to load secrets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSecrets();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API}/secrets`, formData, {
        headers: getAuthHeaders()
      });
      toast.success('API key added successfully');
      setDialogOpen(false);
      setFormData({ provider: '', api_key: '', api_secret: '' });
      fetchSecrets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save secret');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (secretId) => {
    if (!confirm('Are you sure you want to delete this API key?')) return;

    try {
      await axios.delete(`${API}/secrets/${secretId}`, {
        headers: getAuthHeaders()
      });
      toast.success('API key deleted');
      fetchSecrets();
    } catch (error) {
      toast.error('Failed to delete secret');
    }
  };

  const toggleShowKey = (id) => {
    setShowKey(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getCarrierInfo = (provider) => {
    return CARRIERS.find(c => c.value === provider) || { label: provider, description: '' };
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="secrets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">API Secrets</h1>
          <p className="text-slate-500 mt-1">Securely manage your carrier API keys</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-press" data-testid="add-secret-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add API Key
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add API Key</DialogTitle>
              <DialogDescription>
                Connect a shipping carrier by adding your API credentials.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Carrier</Label>
                  <Select
                    value={formData.provider}
                    onValueChange={(value) => setFormData({ ...formData, provider: value })}
                  >
                    <SelectTrigger data-testid="carrier-select">
                      <SelectValue placeholder="Select a carrier" />
                    </SelectTrigger>
                    <SelectContent>
                      {CARRIERS.map((carrier) => (
                        <SelectItem key={carrier.value} value={carrier.value}>
                          {carrier.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="api_key">API Key</Label>
                  <Input
                    id="api_key"
                    type="password"
                    placeholder="Enter your API key"
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                    className="secrets-input"
                    required
                    data-testid="api-key-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="api_secret">API Secret (Optional)</Label>
                  <Input
                    id="api_secret"
                    type="password"
                    placeholder="Enter your API secret"
                    value={formData.api_secret}
                    onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                    className="secrets-input"
                    data-testid="api-secret-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving || !formData.provider || !formData.api_key} data-testid="save-secret-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Key'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Security Notice */}
      <Card className="bg-slate-900 text-white border-slate-800">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h3 className="font-medium text-lg mb-1">Secure Storage</h3>
              <p className="text-slate-400 text-sm">
                Your API keys are encrypted and stored securely. We never share or expose your credentials.
                Keys are only used to connect to carrier APIs on your behalf.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Secrets List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : secrets.length > 0 ? (
        <div className="grid gap-4">
          {secrets.map((secret) => {
            const carrier = getCarrierInfo(secret.provider);
            return (
              <Card key={secret.id} className={`transition-all ${secret.is_active ? '' : 'opacity-50'}`} data-testid={`secret-card-${secret.provider}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center">
                        <Key className="w-6 h-6 text-slate-500" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{carrier.label}</h3>
                          {secret.is_active ? (
                            <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-full">
                              <CheckCircle className="w-3 h-3" />
                              Active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                              <AlertCircle className="w-3 h-3" />
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="font-mono text-sm text-slate-500">
                            {showKey[secret.id] ? secret.masked_key : '••••••••••••'}
                          </code>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => toggleShowKey(secret.id)}
                            data-testid={`toggle-key-${secret.id}`}
                          >
                            {showKey[secret.id] ? (
                              <EyeOff className="w-3 h-3" />
                            ) : (
                              <Eye className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">
                        Added {new Date(secret.created_at).toLocaleDateString()}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        onClick={() => handleDelete(secret.id)}
                        data-testid={`delete-secret-${secret.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-dashed border-2">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium mb-2">No API Keys Added</h3>
            <p className="text-slate-500 mb-6 max-w-sm mx-auto">
              Connect your shipping carriers by adding their API credentials.
            </p>
            <Button onClick={() => setDialogOpen(true)} data-testid="add-first-secret-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First API Key
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Supported Integrations by Category */}
      {['shipping', 'fraud', 'verification', 'custom'].map((category) => {
        const categoryLabels = {
          shipping: 'Shipping Carriers',
          fraud: 'Fraud Analysis & Prevention',
          verification: 'Address & Identity Verification',
          custom: 'Custom Integrations'
        };
        const categoryItems = CARRIERS.filter(c => c.category === category);
        
        return (
          <Card key={category}>
            <CardHeader>
              <CardTitle>{categoryLabels[category]}</CardTitle>
              <CardDescription>
                {category === 'shipping' && 'Connect your shipping carrier accounts'}
                {category === 'fraud' && 'Add fraud detection and prevention services'}
                {category === 'verification' && 'Address validation and identity verification'}
                {category === 'custom' && 'Custom API integrations'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryItems.map((carrier) => {
                  const isConnected = secrets.some(s => s.provider === carrier.value && s.is_active);
                  return (
                    <div 
                      key={carrier.value}
                      className={`p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md ${isConnected ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-900/10' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300'}`}
                      onClick={() => {
                        if (!isConnected) {
                          setFormData({ ...formData, provider: carrier.value });
                          setDialogOpen(true);
                        }
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">{carrier.label}</h4>
                        {isConnected && (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{carrier.description}</p>
                      {!isConnected && (
                        <p className="text-xs text-secondary mt-2">Click to connect</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default SecretsPage;
